import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

// Sales Analytics
interface SalesMetrics {
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
  topSellingItems: {
    itemId: string;
    itemName: { en: string; th: string };
    quantity: number;
    revenue: number;
  }[];
  salesByCategory: {
    categoryId: string;
    categoryName: { en: string; th: string };
    revenue: number;
    orderCount: number;
  }[];
  salesByHour: {
    hour: number;
    revenue: number;
    orderCount: number;
  }[];
  paymentMethods: {
    method: string;
    count: number;
    total: number;
  }[];
}

interface SalesByModifier {
  sweetness: {
    level: string;
    count: number;
  }[];
  toppings: {
    topping: string;
    count: number;
  }[];
  sizes: {
    size: string;
    count: number;
  }[];
}

// Inventory Analytics
interface InventoryMetrics {
  totalItems: number;
  lowStockCount: number;
  outOfStockCount: number;
  expiringCount: number;
  totalValue: number;
  usageByIngredient: {
    ingredientId: string;
    ingredientName: string;
    used: number;
    unit: string;
    cost: number;
  }[];
  stockLevels: {
    ingredientId: string;
    ingredientName: string;
    currentStock: number;
    reorderLevel: number;
    maxLevel: number;
    status: 'low' | 'normal' | 'overstock' | 'out_of_stock';
  }[];
}

// Waste Analytics
interface WasteMetrics {
  totalValue: number;
  totalEntries: number;
  byCategory: {
    category: string;
    count: number;
    value: number;
  }[];
  byIngredient: {
    ingredientId: string;
    ingredientName: string;
    quantity: number;
    unit: string;
    value: number;
  }[];
  trend: {
    date: string;
    count: number;
    value: number;
  }[];
}

// Operations Analytics
interface OperationsMetrics {
  purchaseRequests: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    averageApprovalTime: number; // hours
  };
  purchaseOrders: {
    total: number;
    draft: number;
    sent: number;
    partiallyReceived: number;
    completed: number;
  };
  transfers: {
    total: number;
    requested: number;
    inTransit: number;
    received: number;
    averageDeliveryTime: number; // hours
  };
  kitchenPerformance: {
    averagePrepTime: number; // minutes
    ordersCompleted: number;
    peakHours: {
      hour: number;
      orderCount: number;
    }[];
  };
}

// Promotion Analytics
interface PromotionMetrics {
  totalPromotions: number;
  activePromotions: number;
  totalDiscountGiven: number;
  usageByPromotion: {
    promotionId: string;
    promotionName: { en: string; th: string };
    usageCount: number;
    totalDiscount: number;
    revenueImpact: number;
  }[];
}

// Fetch sales analytics
export function useSalesAnalytics(
  branchId?: string,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['analytics', 'sales', branchId, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await apiClient.get<APIResponse<{ metrics: SalesMetrics }>>(
        `/analytics/sales?${params.toString()}`
      );
      return response.data.data.metrics;
    },
  });
}

// Fetch sales by modifier
export function useSalesByModifier(
  branchId?: string,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['analytics', 'sales-modifiers', branchId, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await apiClient.get<APIResponse<{ data: SalesByModifier }>>(
        `/analytics/sales/modifiers?${params.toString()}`
      );
      return response.data.data.data;
    },
  });
}

// Fetch inventory analytics
export function useInventoryAnalytics(branchId?: string) {
  return useQuery({
    queryKey: ['analytics', 'inventory', branchId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);

      const response = await apiClient.get<APIResponse<{ metrics: InventoryMetrics }>>(
        `/analytics/inventory?${params.toString()}`
      );
      return response.data.data.metrics;
    },
  });
}

// Fetch waste analytics
export function useWasteAnalytics(
  branchId?: string,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['analytics', 'waste', branchId, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await apiClient.get<APIResponse<{ metrics: WasteMetrics }>>(
        `/analytics/waste?${params.toString()}`
      );
      return response.data.data.metrics;
    },
  });
}

// Fetch operations analytics
export function useOperationsAnalytics(
  branchId?: string,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['analytics', 'operations', branchId, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await apiClient.get<APIResponse<{ metrics: OperationsMetrics }>>(
        `/analytics/operations?${params.toString()}`
      );
      return response.data.data.metrics;
    },
  });
}

// Fetch promotion analytics
export function usePromotionAnalytics(
  branchId?: string,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['analytics', 'promotions', branchId, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await apiClient.get<APIResponse<{ metrics: PromotionMetrics }>>(
        `/analytics/promotions?${params.toString()}`
      );
      return response.data.data.metrics;
    },
  });
}

// ================================
// ADVANCED REPORTS TYPES
// ================================

// Product type with expandable products
export interface ProductTypeWithProducts {
  name: string;
  sales: number;
  revenue: number;
  target: number;
  achievementPct: number;
  products: Array<{
    id: string;
    name: string;
    sales: number;
    revenue: number;
    target: number;
    achievementPct: number;
  }>;
}

// Period breakdown for drill-down
export interface PeriodBreakdown {
  periodIndex: number;
  periodLabel: string;
  productTypes: ProductTypeWithProducts[];
}

export interface ProductSalesTrendReport {
  title: { en: string; th: string };
  year: number;
  periodLabel?: string;
  trend: Array<{
    month: string;
    monthIndex: number;
    monthYear?: number;
    trendIndex?: number;
    sales: number;
    orders: number;
    target: number;
  }>;
  average: number;
  target: number;
  productTypes: ProductTypeWithProducts[];
  periodBreakdown: PeriodBreakdown[];
  summary: {
    totalSales: number;
    totalTarget: number;
    achievementPercentage: number;
    categoryCount: number;
    averagePerCategory: number;
  };
}

// Daily Sales Trend (drill-down from monthly)
export interface DailySalesTrendReport {
  title: { en: string; th: string };
  year: number;
  month: string;
  monthIndex: number;
  trend: Array<{
    day: number;
    dayOfWeek: string;
    date: string;
    sales: number;
    orders: number;
    revenue: number;
    target: number;
  }>;
  average: number;
  target: number;
  dayOfWeekBreakdown: Array<{
    dayOfWeek: string;
    dayIndex: number;
    totalSales: number;
    totalOrders: number;
    totalRevenue: number;
    averageSales: number;
    daysCount: number;
  }>;
  productTypes: ProductTypeWithProducts[];
  periodBreakdown: PeriodBreakdown[];
  summary: {
    totalSales: number;
    totalOrders: number;
    totalRevenue: number;
    totalTarget: number;
    achievementPercentage: number;
    peakDay: { day: number; sales: number };
  };
}

// Hourly Sales Trend (drill-down from daily)
export interface HourlySalesTrendReport {
  title: { en: string; th: string };
  date: string;
  year: number;
  month: number;
  day: number;
  trend: Array<{
    hour: number;
    hourLabel: string;
    sales: number;
    orders: number;
    revenue: number;
    target: number;
  }>;
  average: number;
  target: number;
  timePeriodBreakdown: Array<{
    period: string;
    periodName: { en: string; th: string };
    totalSales: number;
    totalOrders: number;
    totalRevenue: number;
    averageSales: number;
    peakHour: string;
    peakSales: number;
  }>;
  productTypes: ProductTypeWithProducts[];
  periodBreakdown: PeriodBreakdown[];
  summary: {
    totalSales: number;
    totalOrders: number;
    totalRevenue: number;
    totalTarget: number;
    achievementPercentage: number;
    peakHour: { hour: number; label: string; sales: number };
  };
}

export interface ProductHierarchyReport {
  title: { en: string; th: string };
  period: {
    current: string;
    previous: string;
  };
  summary: {
    totalSales: number;
    totalPrevMonthSales: number;
    growth: {
      value: number;
      percentage: number;
      direction: 'up' | 'down' | 'stable';
    };
  };
  hierarchy: Array<{
    id: string;
    name: { en: string; th?: string };
    type: 'category';
    sales: number;
    prevMonthSales: number;
    growth: {
      value: number;
      percentage: number;
      direction: string;
    };
    children: Array<{
      id: string;
      name: { en: string; th?: string };
      type: 'product';
      sales: number;
      prevMonthSales: number;
      growth: {
        value: number;
        percentage: number;
        direction: string;
      };
    }>;
  }>;
}

export interface RegionalSalesReport {
  title: { en: string; th: string };
  year: number;
  trend: Array<{
    month: string;
    monthIndex: number;
    sales: number;
  }>;
  average: number;
  districts: Array<{
    district: string;
    branchCount: number;
    currentSales: number;
    prevMonthSales: number;
    growth: {
      value: number;
      percentage: number;
      direction: string;
    };
    average: number;
    status: 'above' | 'below' | 'equal';
  }>;
  summary: {
    totalDistricts: number;
    totalBranches: number;
    totalSales: number;
  };
}

export interface SalesTargetReport {
  title: { en: string; th: string };
  period: {
    month: string;
    year: number;
  };
  summary: {
    actual: number;
    target: number;
    percentage: number;
    status: 'high' | 'medium' | 'low';
  };
  legend: {
    high: { label: { en: string; th: string }; color: string };
    medium: { label: { en: string; th: string }; color: string };
    low: { label: { en: string; th: string }; color: string };
  };
  monthlyAchievement: Array<{
    month: string;
    year: number;
    monthIndex: number;
    actual: number;
    target: number;
    percentage: number;
    status: 'high' | 'medium' | 'low';
  }>;
  branchBreakdown: Array<{
    branchId: string;
    branchName: string;
    district: string;
    actual: number;
    target: number;
    percentage: number;
    status: 'high' | 'medium' | 'low';
  }>;
  filters: {
    districts: string[];
    currentDistrict: string | null;
    currentBranch: string | null;
  };
}

// ================================
// ADVANCED REPORTS HOOKS
// ================================

/**
 * Fetch Product Sales Trend vs Average report (Year view - monthly data)
 * Supports either year or startDate/endDate for rolling periods
 */
export function useProductSalesTrendReport(
  year?: number,
  branchId?: string,
  startDate?: string,
  endDate?: string
) {
  return useQuery({
    queryKey: ['analytics', 'reports', 'product-sales-trend', year, branchId, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      } else if (year) {
        params.append('year', year.toString());
      }
      if (branchId) params.append('branchId', branchId);

      const response = await apiClient.get<APIResponse<ProductSalesTrendReport>>(
        `/analytics/reports/product-sales-trend?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch Daily Sales Trend (drill-down from monthly view)
 */
export function useProductSalesTrendDaily(
  year: number,
  month: number,
  branchId?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ['analytics', 'reports', 'product-sales-trend', 'daily', year, month, branchId],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('year', year.toString());
      params.append('month', month.toString());
      if (branchId) params.append('branchId', branchId);

      const response = await apiClient.get<APIResponse<DailySalesTrendReport>>(
        `/analytics/reports/product-sales-trend/daily?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/**
 * Fetch Hourly Sales Trend (drill-down from daily view)
 */
export function useProductSalesTrendHourly(
  year: number,
  month: number,
  day: number,
  branchId?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ['analytics', 'reports', 'product-sales-trend', 'hourly', year, month, day, branchId],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('year', year.toString());
      params.append('month', month.toString());
      params.append('day', day.toString());
      if (branchId) params.append('branchId', branchId);

      const response = await apiClient.get<APIResponse<HourlySalesTrendReport>>(
        `/analytics/reports/product-sales-trend/hourly?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/**
 * Fetch Product Hierarchy with Growth report
 */
export function useProductHierarchyReport(year?: number, branchId?: string) {
  return useQuery({
    queryKey: ['analytics', 'reports', 'product-hierarchy', year, branchId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (year) params.append('year', year.toString());
      if (branchId) params.append('branchId', branchId);

      const response = await apiClient.get<APIResponse<ProductHierarchyReport>>(
        `/analytics/reports/product-hierarchy?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch Regional/District Sales Analysis report
 */
export function useRegionalSalesReport(year?: number) {
  return useQuery({
    queryKey: ['analytics', 'reports', 'regional-sales', year],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (year) params.append('year', year.toString());

      const response = await apiClient.get<APIResponse<RegionalSalesReport>>(
        `/analytics/reports/regional-sales?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch Sales Target Dashboard report
 */
export function useSalesTargetReport(branchId?: string, district?: string, month?: string) {
  return useQuery({
    queryKey: ['analytics', 'reports', 'sales-target', branchId, district, month],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (district) params.append('district', district);
      if (month) params.append('month', month);

      const response = await apiClient.get<APIResponse<SalesTargetReport>>(
        `/analytics/reports/sales-target?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export type {
  SalesMetrics,
  SalesByModifier,
  InventoryMetrics,
  WasteMetrics,
  OperationsMetrics,
  PromotionMetrics,
};
